/**
 * Minimal JSONPath helpers for field-mapping inputs. Matches the backend's
 * `$.detail.X` dot/bracket convention — enough to surface the fields present in
 * a sample event and preview what a mapping resolves to. Not a full JSONPath
 * engine (no filters, wildcards, or recursive descent).
 */

export interface JsonPathEntry {
  path: string;
  value: unknown;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Flatten an object into its leaf (and array-scalar) paths, e.g.
 * `$.detail.AlarmName`, `$.resources[0]`. Arrays of objects recurse with an
 * index. */
export function extractJsonPaths(obj: unknown, base = '$'): JsonPathEntry[] {
  const out: JsonPathEntry[] = [];
  const walk = (node: unknown, path: string) => {
    if (isPlainObject(node)) {
      for (const [key, value] of Object.entries(node)) {
        walk(value, `${path}.${key}`);
      }
    } else if (Array.isArray(node)) {
      node.forEach((item, i) => walk(item, `${path}[${i}]`));
    } else {
      out.push({ path, value: node });
    }
  };
  walk(obj, base);
  return out;
}

/** Resolve a `$.a.b[0].c` path against an object. Returns undefined if any
 * segment is missing. */
export function resolveJsonPath(path: string, obj: unknown): unknown {
  if (!path) return undefined;
  const segments = path
    .replace(/^\$\.?/, '')
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);
  let current: unknown = obj;
  for (const seg of segments) {
    if (Array.isArray(current)) {
      current = current[Number(seg)];
    } else if (isPlainObject(current)) {
      current = current[seg];
    } else {
      return undefined;
    }
    if (current === undefined) return undefined;
  }
  return current;
}

/** A value is treated as a JSONPath when it starts with `$`. */
export function looksLikePath(value: string): boolean {
  return value.trim().startsWith('$');
}

/** Short, single-line preview of a resolved value for inline display. */
export function previewValue(value: unknown, max = 60): string {
  if (value === undefined) return '';
  if (typeof value === 'string') return value.length > max ? `${value.slice(0, max)}…` : value;
  const str = JSON.stringify(value);
  return str.length > max ? `${str.slice(0, max)}…` : str;
}
